'use client';

import { c as _c } from "react/compiler-runtime";
import { AboveValueConfigKind, BelowValueConfigKind, BetweenValueConfigKind, CohortFilterGranularity, ExactlyValueConfigKind, TimeRangeConfig } from '@/api/generated/model';
import { Input } from '@/comps/primitives/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/comps/primitives/select';
import { nullableNumberToString } from '@/utils/numbers.util';
import { cn } from '@/utils/shad';
import { translate } from '@/utils/translations';
import { useEffect, useState } from 'react';
export const COHORT_NUMERIC_COMPARATOR_KINDS = [AboveValueConfigKind.above, BelowValueConfigKind.below, ExactlyValueConfigKind.exactly, BetweenValueConfigKind.between] as const;
export type CohortNumericComparatorKind = (typeof COHORT_NUMERIC_COMPARATOR_KINDS)[number];
export function getNumericComparatorLabel(kind: CohortNumericComparatorKind): string {
  return translate('value_config_kind', kind);
}
export function CohortInlineNumberInput(t0) {
  const $ = _c(22);
  const {
    value,
    onChange,
    className,
    invalid,
    integersOnly
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = () => nullableNumberToString(value);
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [text, setText] = useState(t1);
  const [focused, setFocused] = useState(false);
  let t2;
  let t3;
  if ($[2] !== focused || $[3] !== value) {
    t2 = () => {
      if (!focused) {
        setText(nullableNumberToString(value));
      }
    };
    t3 = [value, focused];
    $[2] = focused;
    $[3] = value;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  const t4 = integersOnly ? 1 : undefined;
  let t5;
  if ($[6] !== integersOnly || $[7] !== onChange) {
    t5 = event => {
      const raw = event.currentTarget.value;
      setText(raw);
      if (raw === "") {
        onChange(null);
        return;
      }
      const parsed = Number(raw);
      if (!Number.isNaN(parsed)) {
        onChange(integersOnly ? Math.round(parsed) : parsed);
      }
    };
    $[6] = integersOnly;
    $[7] = onChange;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setFocused(true);
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== value) {
    t7 = () => {
      setFocused(false);
      setText(nullableNumberToString(value));
    };
    $[10] = value;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = invalid && "border-destructive focus-visible:ring-destructive";
  let t9;
  if ($[12] !== className || $[13] !== t8) {
    t9 = cn("h-8 w-16 [appearance:textfield] border-zinc-700 bg-transparent px-2 text-sm [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none", t8, className);
    $[12] = className;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== invalid || $[16] !== t4 || $[17] !== t5 || $[18] !== t7 || $[19] !== t9 || $[20] !== text) {
    t10 = <Input type="number" step={t4} value={text} onChange={t5} onFocus={t6} onBlur={t7} aria-invalid={invalid} className={t9} />;
    $[15] = invalid;
    $[16] = t4;
    $[17] = t5;
    $[18] = t7;
    $[19] = t9;
    $[20] = text;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
export function CohortComparatorSelect(t0) {
  const $ = _c(10);
  const {
    value,
    onChange,
    popoverContainer
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = next => onChange(next as CohortNumericComparatorKind);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectTrigger className="h-8 w-30 border-zinc-700 bg-transparent text-sm"><SelectValue /></SelectTrigger>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = COHORT_NUMERIC_COMPARATOR_KINDS.map(_temp);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== popoverContainer) {
    t4 = <SelectContent container={popoverContainer} className="z-1000!">{t3}</SelectContent>;
    $[4] = popoverContainer;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4 || $[8] !== value) {
    t5 = <Select value={value} onValueChange={t1}>{t2}{t4}</Select>;
    $[6] = t1;
    $[7] = t4;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function _temp(kind) {
  return <SelectItem key={kind} value={kind}>{getNumericComparatorLabel(kind)}</SelectItem>;
}
export function CohortGranularitySelect(t0) {
  const $ = _c(17);
  const {
    value,
    onChange,
    options,
    popoverContainer,
    className
  } = t0;
  let t1;
  if ($[0] !== onChange) {
    t1 = next => onChange(next as CohortFilterGranularity);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className) {
    t2 = cn("h-8 w-25 border-zinc-700 bg-transparent text-sm", className);
    $[2] = className;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectValue />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <SelectTrigger className={t2}>{t3}</SelectTrigger>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== options) {
    t5 = options.map(_temp2);
    $[7] = options;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== popoverContainer || $[10] !== t5) {
    t6 = <SelectContent container={popoverContainer} className="z-1000!">{t5}</SelectContent>;
    $[9] = popoverContainer;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t1 || $[13] !== t4 || $[14] !== t6 || $[15] !== value) {
    t7 = <Select value={value} onValueChange={t1}>{t4}{t6}</Select>;
    $[12] = t1;
    $[13] = t4;
    $[14] = t6;
    $[15] = value;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}
function _temp2(option) {
  return <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>;
}
export const WINDOW_GRANULARITY_OPTIONS = [{
  value: CohortFilterGranularity.day,
  label: 'days'
}, {
  value: CohortFilterGranularity.week,
  label: 'weeks'
}, {
  value: CohortFilterGranularity.month,
  label: 'months'
}];
export const PER_GRANULARITY_OPTIONS = [{
  value: CohortFilterGranularity.conversation,
  label: 'avg per conversation'
}, {
  value: CohortFilterGranularity.day,
  label: 'per day'
}, {
  value: CohortFilterGranularity.week,
  label: 'per week'
}, {
  value: CohortFilterGranularity.month,
  label: 'per month'
}];
export function CohortDurationUnitSelect(t0) {
  const $ = _c(15);
  const {
    value,
    onChange,
    options,
    popoverContainer,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("h-8 w-25 border-zinc-700 bg-transparent text-sm", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectValue />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <SelectTrigger className={t1}>{t2}</SelectTrigger>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== options) {
    t4 = options.map(_temp3);
    $[5] = options;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== popoverContainer || $[8] !== t4) {
    t5 = <SelectContent container={popoverContainer} className="z-1000!">{t4}</SelectContent>;
    $[7] = popoverContainer;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== onChange || $[11] !== t3 || $[12] !== t5 || $[13] !== value) {
    t6 = <Select value={value} onValueChange={onChange}>{t3}{t5}</Select>;
    $[10] = onChange;
    $[11] = t3;
    $[12] = t5;
    $[13] = value;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function _temp3(option) {
  return <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>;
}
export type CohortFilterEditorProps = {
  projectId: string;
  timeRange: TimeRangeConfig;
  popoverContainer?: Element | null;
  showValidation?: boolean;
};